import { useCallback, useEffect, useMemo, useState } from 'react';

import { useCity } from '../features/city/useCity';
import { deviceStorage } from './storage';
import { quoted, type ToastMessage } from './useToasts';

/**
 * The user's RSVPs, one status per event, per city: `cg.rsvp.<city>`.
 *
 *   going — MCW's "Add to Schedule". Full opacity on My Schedule.
 *   maybe — MCW's "Save for Later". 50% opacity.
 *
 * Setting one replaces the other; setting null clears it. The buttons clear
 * the active status when it is tapped again (the caller decides that, since
 * only it knows which button was pressed). No undo: the toast says what happened.
 */

export type RsvpStatus = 'going' | 'maybe';

type RsvpMap = Record<string, RsvpStatus>;

const keyFor = (city: string) => `cg.rsvp.${city}`;

function read(city: string): RsvpMap {
  try {
    const parsed: unknown = JSON.parse(deviceStorage.getItem(keyFor(city)) ?? '{}');
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: RsvpMap = {};
    for (const [id, status] of Object.entries(parsed)) {
      if (status === 'going' || status === 'maybe') out[id] = status;
    }
    return out;
  } catch {
    return {};
  }
}

export interface Rsvp {
  statusOf: (id: string) => RsvpStatus | null;
  /** `title` names the event in the toast. */
  setStatus: (id: string, status: RsvpStatus | null, title: string) => void;
  goingIds: ReadonlySet<string>;
  maybeIds: ReadonlySet<string>;
  /** Going or Maybe: what the Saved pill shows. */
  allIds: ReadonlySet<string>;
  /** Every id and its status, for the backup. */
  map: Readonly<Record<string, RsvpStatus>>;
  /** Replaces the whole list at once, silently: a restore. */
  replace: (map: Record<string, RsvpStatus>) => void;
  clear: () => void;
}

const MESSAGE: Record<RsvpStatus | 'none', string> = {
  going: 'marked Going.',
  maybe: 'marked Maybe.',
  none: 'removed from Saved.',
};

/** Told what happened, to show it as a toast; `status` and `id` let the caller attach an action. */
export type RsvpNotify = (message: ToastMessage, status: RsvpStatus | null, id: string) => void;

export function useRsvp(notify?: RsvpNotify): Rsvp {
  const { city } = useCity();
  const [state, setState] = useState<{ city: string; map: RsvpMap }>(() => ({ city, map: read(city) }));
  // Another city: its own list.
  if (state.city !== city) setState({ city, map: read(city) });
  const map = state.city === city ? state.map : read(city);

  useEffect(() => {
    try {
      deviceStorage.setItem(keyFor(state.city), JSON.stringify(state.map));
    } catch {
      // Site data blocked. RSVPs still work for this session.
    }
  }, [state]);

  const setStatus = useCallback(
    (id: string, status: RsvpStatus | null, title: string) => {
      setState((s) => {
        const next = { ...s.map };
        if (status) next[id] = status;
        else delete next[id];
        return { ...s, map: next };
      });
      notify?.(quoted(title, MESSAGE[status ?? 'none']), status, id);
    },
    [notify],
  );

  const clear = useCallback(() => setState((s) => ({ ...s, map: {} })), []);
  const replace = useCallback((next: Record<string, RsvpStatus>) => setState((s) => ({ ...s, map: { ...next } })), []);

  return useMemo(() => {
    const going = new Set<string>();
    const maybe = new Set<string>();
    for (const [id, status] of Object.entries(map)) (status === 'going' ? going : maybe).add(id);
    return {
      statusOf: (id: string) => map[id] ?? null,
      setStatus,
      goingIds: going,
      maybeIds: maybe,
      allIds: new Set([...going, ...maybe]),
      map,
      replace,
      clear,
    };
  }, [map, setStatus, replace, clear]);
}
